import copy from "copy-to-clipboard"

import { mtLocation } from "@/lib/mt-location"

export type WebShareResult = "shared" | "copied" | "canceled" | "copy-failed"
export type WebShareFilesResult = "shared" | "canceled" | "unsupported"

/**
 * `navigator.share` can exist even when Permissions Policy blocks its use.
 * `canShare` accounts for that policy as well as the share data itself.
 */
export function canUseWebShare(data?: ShareData): boolean {
  if (
    typeof navigator === "undefined" ||
    typeof navigator.share !== "function"
  ) {
    return false
  }

  if (typeof navigator.canShare !== "function") {
    return true
  }

  try {
    return navigator.canShare(data ?? { url: mtLocation.href })
  } catch {
    return false
  }
}

/**
 * Shares a URL when the browser permits it and copies it when sharing is
 * blocked (for example by expired user activation or iframe policy).
 * User cancellation is intentionally a no-op. Unexpected errors still reject
 * so callers can report them.
 */
export async function shareUrlWithFallback(
  url: string
): Promise<WebShareResult> {
  const data = { url }

  if (!canUseWebShare(data)) {
    return copyUrl(url)
  }

  try {
    await navigator.share(data)
    return "shared"
  } catch (error) {
    const name = errorName(error)
    if (name === "AbortError") {
      return "canceled"
    }
    if (name === "NotAllowedError") {
      return copyUrl(url)
    }
    throw error
  }
}

/**
 * Shares files when the browser permits it. User cancellation is a no-op.
 * Other failures return "unsupported" so callers can fall back to download.
 */
export async function shareFiles(files: File[]): Promise<WebShareFilesResult> {
  const data = { files }

  if (!canUseWebShare(data)) {
    return "unsupported"
  }

  try {
    await navigator.share(data)
    return "shared"
  } catch (error) {
    const name = errorName(error)
    if (name === "AbortError") {
      return "canceled"
    }
    if (name === "NotAllowedError" || name === "TypeError") {
      return "unsupported"
    }
    throw error
  }
}

function copyUrl(url: string): WebShareResult {
  try {
    return copy(url) ? "copied" : "copy-failed"
  } catch {
    return "copy-failed"
  }
}

function errorName(error: unknown): string | undefined {
  if (typeof error !== "object" || error === null || !("name" in error)) {
    return undefined
  }

  const name = error.name
  return typeof name === "string" ? name : undefined
}
